
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toastService } from "@/utils/toastService";
import { Media } from "@/components/media/types";
import { extractVideoThumbnail } from "@/components/media/utils/thumbnailExtractor";
import { formatBytes, recalculateStorage } from "@/hooks/useMemorialStorage";

interface UploadOptions {
  memorialId: string;
  storyId?: string;
  lifeStageId?: string;
  isCompressed?: boolean;
  originalSize?: number;
  thumbnailFile?: File;
}

const checkStorageLimit = async (
  memorialId: string,
  fileSize: number
): Promise<{ withinLimit: boolean; errorMessage?: string }> => {
  try {
    const { data, error } = await supabase
      .rpc('check_memorial_storage_limit', {
        memorial_uuid: memorialId,
        additional_size_bytes: fileSize
      });

    if (error) {
      return {
        withinLimit: false,
        errorMessage: `Failed to check storage limit: ${error.message}`
      };
    }

    const result = data[0];
    
    if (!result.within_limit) {
      const currentUsage = formatBytes(result.current_usage);
      const storageLimit = formatBytes(result.storage_limit);
      const availableSpace = formatBytes(result.available_space);
      
      return {
        withinLimit: false,
        errorMessage: `Storage limit exceeded. Currently using ${currentUsage} of ${storageLimit}. Available space: ${availableSpace}. File size: ${formatBytes(fileSize)}.`
      };
    }

    return { withinLimit: true };
  } catch (error) {
    return {
      withinLimit: false,
      errorMessage: 'Failed to verify storage limit. Please try again.'
    };
  }
};

export const useMediaUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      file, 
      options,
      onProgress
    }: { 
      file: File; 
      options: UploadOptions;
      onProgress?: (percentage: number) => void;
    }): Promise<Media> => {
      try {
        const isVideo = file.type.startsWith('video/');

        // Validate file size — 200MB for videos (Cloudflare Stream), 50MB for images/audio (Supabase Storage)
        const maxSize = isVideo ? 200 * 1024 * 1024 : 50 * 1024 * 1024;
        if (file.size > maxSize) {
          throw new Error(`File is too large. Maximum size is ${isVideo ? '200MB' : '50MB'}.`);
        }

        // --- VIDEO PATH: Upload to Cloudflare Stream ---
        if (isVideo) {
          // 1. Get direct upload URL from our edge function
          const { data: { session } } = await supabase.auth.getSession();
          const { data: streamData, error: streamError } = await supabase.functions.invoke('create-stream-upload', {
            body: { maxDurationSeconds: 600 },
          });

          if (streamError || !streamData?.uploadURL) {
            throw new Error(streamError?.message || 'Failed to get video upload URL');
          }

          const { uploadURL, uid: streamId } = streamData;

          // 2. Upload file directly to Cloudflare Stream with progress
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable && onProgress) {
                onProgress((e.loaded / e.total) * 100);
              }
            });

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                reject(new Error(`Video upload failed with status ${xhr.status}`));
              }
            });

            xhr.addEventListener('error', () => reject(new Error('Video upload failed')));

            // Cloudflare direct upload uses POST with FormData
            const formData = new FormData();
            formData.append('file', file);

            xhr.open('POST', uploadURL);
            xhr.send(formData);
          });

          // 3. Create media record with stream_id (Cloudflare handles transcoding + thumbnails)
          const thumbnailUrl = `https://videodelivery.net/${streamId}/thumbnails/thumbnail.jpg`;
          const mediaData: Record<string, string | number | null> = {
            url: thumbnailUrl,
            thumbnail_url: thumbnailUrl,
            media_type: 'video',
            memorial_id: options.memorialId,
            file_size: file.size,
            story_id: options.storyId || null,
            memorial_life_stage_id: options.lifeStageId || null,
            processing_status: 'completed',
            stream_id: streamId,
          };

          const { data: media, error: dbError } = await supabase
            .from('media')
            .insert(mediaData)
            .select()
            .single();

          if (dbError) {
            throw new Error(`Database insert failed: ${dbError.message}`);
          }

          return media as Media;
        }

        // --- IMAGE/AUDIO PATH: Upload to Supabase Storage (unchanged) ---

        // Check storage limit before uploading
        const { withinLimit, errorMessage } = await checkStorageLimit(options.memorialId, file.size);
        if (!withinLimit) {
          throw new Error(errorMessage || 'Storage limit exceeded');
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;

        // Determine storage path based on context
        let filePath: string;
        if (options.storyId) {
          filePath = `stories/${options.storyId}/${fileName}`;
        } else if (options.lifeStageId) {
          filePath = `life-stages/${options.lifeStageId}/${fileName}`;
        } else {
          filePath = `gallery/${options.memorialId}/${fileName}`;
        }

        // Handle thumbnail upload
        let thumbnailUrl = undefined;

        // If pre-generated thumbnail provided (from compression), upload it
        if (options.thumbnailFile) {
          const thumbExt = options.thumbnailFile.type === 'image/jpeg' ? 'jpg' : 'webp';
          const thumbContentType = options.thumbnailFile.type || 'image/webp';
          const thumbnailFileName = `${crypto.randomUUID()}-thumbnail.${thumbExt}`;
          const thumbnailPath = `${filePath.split('/').slice(0, -1).join('/')}/thumbnails/${thumbnailFileName}`;

          const { error: thumbnailError } = await supabase.storage
            .from('memorials')
            .upload(thumbnailPath, options.thumbnailFile, {
              contentType: thumbContentType,
              upsert: true
            });

          if (!thumbnailError) {
            const { data: { publicUrl } } = supabase.storage
              .from('memorials')
              .getPublicUrl(thumbnailPath);

            thumbnailUrl = publicUrl;
          }
        }

        // Upload to Supabase Storage with progress tracking
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
              onProgress((e.loaded / e.total) * 100);
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => reject(new Error('Upload failed')));

          supabase.auth.getSession().then(({ data: { session } }) => {
            const token = session?.access_token;
            const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/memorials/${filePath}`;

            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.setRequestHeader('x-upsert', 'false');
            xhr.send(file);
          }).catch(reject);
        });

        // Get public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('memorials')
          .getPublicUrl(filePath);

        // Determine media type
        const mediaType = file.type.startsWith('image/') ? 'image' : 'audio';

        // Create media database record
        const mediaData: Record<string, string | number | null> = {
          url: publicUrl,
          media_type: mediaType,
          memorial_id: options.memorialId,
          file_size: file.size,
          story_id: options.storyId || null,
          memorial_life_stage_id: options.lifeStageId || null,
          processing_status: 'completed'
        };

        if (options.isCompressed) {
          mediaData.compressed_url = publicUrl;
          mediaData.original_url = null;
        } else {
          mediaData.compressed_url = null;
          mediaData.original_url = publicUrl;
        }

        if (thumbnailUrl) {
          mediaData.thumbnail_url = thumbnailUrl;
        }

        const { data: media, error: dbError } = await supabase
          .from('media')
          .insert(mediaData)
          .select()
          .single();

        if (dbError) {
          throw new Error(`Database insert failed: ${dbError.message}`);
        }

        return {
          ...media,
          thumbnail_url: thumbnailUrl
        } as Media;
      } catch (error) {
        console.error('[useMediaUpload] Upload failed:', error);
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error('[useMediaUpload] Mutation error:', error);
      toastService.error(`Failed to upload media: ${error.message}`);
    },
    onSuccess: async (media: Media) => {
      toastService.success("Media uploaded successfully");
      
      // Invalidate relevant caches
      queryClient.invalidateQueries({ queryKey: ['gallery-photos', media.memorial_id] });
      queryClient.invalidateQueries({ queryKey: ['optimized-memorial-data', media.memorial_id] });
      
      if (media.story_id) {
        queryClient.invalidateQueries({ queryKey: ['story-media', media.story_id] });
      }
      if (media.memorial_life_stage_id) {
        queryClient.invalidateQueries({ queryKey: ['life-stage-media', media.memorial_life_stage_id] });
      }
      
      // Recalculate storage
      await recalculateStorage(media.memorial_id, queryClient);
    }
  });
};
